import React, { useState, useCallback } from 'react';
import { cn } from '../../../../lib/utils';
import debounce from '../../../../utils/debounce';

/**
 * Ultra-Compact Notes Panel Component
 * Minimal design with integrated toggle buttons in TextField corner
 */
const NotesPanel = ({ internalNotes, feedbackToCreator, onNotesUpdate }) => {
  // Component state
  const [noteType, setNoteType] = useState('internal');

  // Debounced update function
  const debounceUpdate = useCallback(
    debounce((field, value) => {
      onNotesUpdate({ [field]: value });
    }, 500),
    [onNotesUpdate]
  );

  // Handle note change based on current type
  const handleNoteChange = (event) => {
    const value = event.target.value;
    const field = noteType === 'internal' ? 'internalNotes' : 'feedbackToCreator';
    debounceUpdate(field, value);
  };

  // Get current note value and placeholder
  const currentValue = noteType === 'internal' ? internalNotes : feedbackToCreator;
  const placeholder = noteType === 'internal'
    ? 'Internal notes (private)...'
    : 'Creator feedback (shared)...';

  return (
    <div>
      {/* Simple toggle buttons on the right */}
      <div className="flex justify-end mb-1.5">
        <div className="inline-flex">
          <button
            onClick={() => setNoteType('internal')}
            className={cn(
              'px-3 py-1 text-[0.7rem] h-[26px] min-w-[65px] border',
              noteType === 'internal'
                ? 'font-semibold text-accent border-accent border-2'
                : 'font-normal text-text-secondary border-glass-border hover:border-accent'
            )}
          >
            Internal
          </button>
          <button
            onClick={() => setNoteType('feedback')}
            className={cn(
              'px-3 py-1 text-[0.7rem] h-[26px] min-w-[65px] border',
              noteType === 'feedback'
                ? 'font-semibold text-gray-400 border-gray-400 border-2'
                : 'font-normal text-text-secondary border-glass-border hover:border-gray-400'
            )}
          >
            Feedback
          </button>
        </div>
      </div>

      {/* Text field */}
      <textarea
        key={noteType}
        rows={2}
        placeholder={placeholder}
        defaultValue={currentValue}
        onChange={handleNoteChange}
        className={cn(
          'w-full text-xs leading-snug min-h-[60px] p-2 resize-none',
          'bg-surface-0 text-text-primary placeholder:text-text-tertiary',
          'border focus:outline-none',
          noteType === 'feedback'
            ? 'border-gray-400/40 focus:border-gray-400'
            : 'border-glass-border focus:border-accent'
        )}
      />
    </div>
  );
};

export default NotesPanel;
